import { useEffect, useMemo, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { TSignedMeditation } from "~/features/meditations/types";
import { useApiClient } from "~/hooks/use-api-client";

// AIDEV-NOTE: React Query hook hitting /api/meditation.status
export function useMeditationsBySession(
    sessionId: string,
    opts?: { enabled?: boolean; sessionToken: string },
) {
    const api = useApiClient();
    const qc = useQueryClient();
    const query = useQuery<TSignedMeditation[], Error>({
        queryKey: ["meditations", "session", sessionId],
        enabled: !!sessionId && (opts?.enabled ?? true),
        staleTime: 60_000,
        queryFn: async () => {
            const res = await api.get("/meditation/status", {
                params: { sessionId, sessionToken: opts?.sessionToken },
            });
            if (!res.ok) throw new Error("Failed to fetch meditations");
            const data = await res.json();
            return (data?.meditations ?? data ?? []) as TSignedMeditation[];
        },
    });

    // AIDEV-NOTE: Conditional polling – only when needed or explicitly requested
    const [shouldPoll, setShouldPoll] = useState(false);
    const hasInFlight = useMemo(
        () =>
            (query.data || []).some(
                (m) => m.status === "PENDING" || m.status === "IN_PROGRESS",
            ),
        [query.data],
    );

    useEffect(() => {
        if (hasInFlight) setShouldPoll(true);
    }, [hasInFlight]);

    useEffect(() => {
        if (!shouldPoll) return;
        const id = setInterval(() => {
            // AIDEV-NOTE: Use refetchQueries for immediate update during polling
            qc.refetchQueries({
                queryKey: ["meditations", "session", sessionId],
            });
        }, 5000);
        return () => clearInterval(id);
    }, [shouldPoll, qc, sessionId]);

    // Expose a way to trigger polling when we enqueue a generation
    const requestPolling = () => setShouldPoll(true);
    return { ...query, requestPolling } as const;
}
